import { useEffect, useState } from "react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { Shield, DollarSign, TrendingUp, Users, Activity } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [pwd, setPwd] = useState(() => localStorage.getItem("naevo_admin_pwd") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const load = async (password, d) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/revenue", { params: { password, days: d } });
      setData(r.data);
      localStorage.setItem("naevo_admin_pwd", password);
      setPwd(password);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Admin fetch failed");
      localStorage.removeItem("naevo_admin_pwd");
      setPwd("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pwd) load(pwd, days);
    // eslint-disable-next-line
  }, [days]);

  if (!pwd) {
    return (
      <div>
        <SiteNav />
        <main className="mx-auto max-w-md px-6 py-20">
          <div className="rounded-3xl border border-white/10 bg-[#0C0914] p-8" data-testid="admin-gate">
            <div className="flex items-center gap-3 mb-4 text-magenta">
              <Shield className="h-6 w-6" />
              <div className="font-display text-2xl text-white">Admin · Markup Dashboard</div>
            </div>
            <p className="text-sm text-white/50 mb-5">
              Enter your <code className="text-magenta">ADMIN_PASSWORD</code> secret to see commission by user + market.
            </p>
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin password"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white focus:border-magenta focus:outline-none"
              data-testid="admin-pwd-input"
              onKeyDown={(e) => e.key === "Enter" && load(input, days)}
            />
            <button
              onClick={() => load(input, days)}
              className="btn-magenta rounded-xl w-full h-12 mt-4 font-medium"
              data-testid="admin-unlock-btn"
            >
              Unlock
            </button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div>
      <SiteNav />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-magenta flex items-center gap-2">
              <Shield className="h-3.5 w-3.5" /> Admin
            </div>
            <h1 className="font-display text-4xl text-white mt-2">Markup Dashboard</h1>
            <p className="text-sm text-white/50 mt-1">Estimated commission (stake × {((data?.markup_rate || 0.01) * 100).toFixed(2)}%)</p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`rounded-full px-4 py-1.5 text-xs font-medium ${days === d ? "btn-magenta" : "btn-ghost"}`}
                data-testid={`admin-window-${d}`}
              >
                {d}d
              </button>
            ))}
            <button
              onClick={() => { localStorage.removeItem("naevo_admin_pwd"); setPwd(""); setData(null); }}
              className="btn-ghost rounded-full px-4 py-1.5 text-xs"
              data-testid="admin-logout"
            >
              Lock
            </button>
          </div>
        </div>

        {loading && !data && <div className="text-white/50">Loading…</div>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
              <StatCard icon={DollarSign} label="Markup earned" value={`$${data.totals.markup.toFixed(2)}`} tone="emerald" />
              <StatCard icon={TrendingUp} label="Total stake" value={`$${data.totals.stake.toFixed(2)}`} />
              <StatCard icon={Activity} label="Trades" value={data.totals.trades.toLocaleString()} />
              <StatCard icon={Users} label="Users" value={data.totals.unique_users.toLocaleString()} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <Table title="Revenue by market" rows={data.by_market} nameKey="symbol" />
              <Table title="Top users (by markup)" rows={data.by_user} nameKey="user" />
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0C0914] p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/40">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`font-mono-naevo text-3xl mt-2 ${tone === "emerald" ? "text-emerald-400" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Table({ title, rows, nameKey }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0C0914] p-5" data-testid={`admin-table-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-xs uppercase tracking-widest text-white/40 mb-4">{title}</div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-white/40 text-sm">No trades yet in this window.</div>}
        {rows.slice(0, 15).map((r, i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
            <div>
              <div className="text-sm font-medium text-white">{r[nameKey]}</div>
              <div className="text-[11px] text-white/40">{r.trades} trades · ${r.stake.toFixed(2)} stake</div>
            </div>
            <div className="font-mono-naevo text-emerald-400">+${r.markup.toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
